import React from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const DraftIndicator = ({
  draftInfo,
  onLoadDraft,
  onClearDraft,
  onSaveDraft,
  showDialog = false,
  onDialogClose,
  draftProgressInfo = null
}) => {
  const { hasDraft, age } = draftInfo;

  const handleLoadDraft = () => {
    onLoadDraft();
    if (onDialogClose) onDialogClose();
  };

  const handleClearDraft = () => {
    onClearDraft();
    if (onDialogClose) onDialogClose();
  };

  if (showDialog) {
    return (
      <Dialog open={showDialog} onClose={onDialogClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RestoreIcon color="primary" />
          Welcome Back! Draft Available
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <strong>Great news!</strong> We found your saved progress from {age}. 
            You can pick up exactly where you left off with all your information intact.
          </Alert>
          
          {draftProgressInfo && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Your Progress: {draftProgressInfo.percentage}% Complete
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={draftProgressInfo.percentage} 
                sx={{ mb: 2, height: 8, borderRadius: 4 }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {draftProgressInfo.completedCount} of {draftProgressInfo.totalCount} steps completed
              </Typography>
              
              {draftProgressInfo.summary.length > 0 && (
                <List dense>
                  {draftProgressInfo.summary.map((item, index) => (
                    <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircleIcon color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={item.replace('✅ ', '')} 
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              
              {draftProgressInfo.nextStepName !== 'Submission Ready' && (
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 'medium' }}>
                  Next step: {draftProgressInfo.nextStepName}
                </Typography>
              )}
            </Box>
          )}
          
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            What would you like to do?
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Box sx={{ p: 2, border: 1, borderColor: 'primary.main', borderRadius: 1, bgcolor: 'primary.50' }}>
              <Typography variant="body1" fontWeight="medium" color="primary">
                📋 Continue with Draft (Recommended)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                All your form data, uploaded documents, and progress will be restored. 
                This is the fastest way to complete your onboarding.
              </Typography>
            </Box>
            
            <Box sx={{ p: 2, border: 1, borderColor: 'grey.300', borderRadius: 1 }}>
              <Typography variant="body1" fontWeight="medium">
                🆕 Start Fresh
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Begin the form from the beginning. Your draft will remain saved in case you change your mind.
              </Typography>
            </Box>
            
            <Box sx={{ p: 2, border: 1, borderColor: 'error.main', borderRadius: 1, bgcolor: 'error.50' }}>
              <Typography variant="body1" fontWeight="medium" color="error">
                🗑️ Delete Draft
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Permanently remove the saved draft and start completely fresh. This cannot be undone.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={onDialogClose} color="inherit" variant="outlined">
            Start Fresh
          </Button>
          <Button
            onClick={handleClearDraft}
            color="error"
            startIcon={<DeleteIcon />}
            variant="outlined"
          >
            Delete Draft
          </Button>
          <Button
            onClick={handleLoadDraft}
            color="primary"
            variant="contained"
            startIcon={<RestoreIcon />}
            size="large"
          >
            Continue with Draft
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (!hasDraft) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 2,
        p: 2,
        bgcolor: 'success.50',
        borderRadius: 1,
        border: 1,
        borderColor: 'success.200',
        mt: 4,
      }}
    >
      <Chip
        icon={<SaveIcon />}
        label={`Auto-saved`} //${age}
        color="success"
        variant="filled"
        size="small"
      />
      
      <Typography variant="body2" color="success.dark" sx={{ flexGrow: 1, fontSize: '12px' }}>
        Your progress is being automatically saved as you work
      </Typography>
      
      <Button
        size="small"
        onClick={onSaveDraft}
        startIcon={<SaveIcon />}
        variant="outlined"
        color="success"
      >
        Save Now
      </Button>
      
      <Button
        size="small"
        onClick={onClearDraft}
        startIcon={<DeleteIcon />}
        color="error"
        variant="text"
      >
        Clear Draft
      </Button>
    </Box>
  );
};

export default DraftIndicator;